import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { auth } from "@/lib/firebase";
import { createLead, fetchLeadsOrdered, computeDashboardStats } from "@/lib/leads";
import {
  Building2, Droplets, TrendingUp, Users, FileText, Target,
  Briefcase, Landmark, ArrowRight, CheckCircle2,
  BarChart3, Mail, ChevronRight,
  Sparkles, Rocket, Award
} from "lucide-react";

// Area icons mapping
const AREA_ICONS = {
  patrimoniale: Building2,
  tesoreria: Droplets,
  trazione: TrendingUp,
  team: Users,
  documentazione: FileText,
  strategico: Target
};

// ─────────────────────────────────────────────────────────────────────────────
// DATI QUESTIONARIO — 6 aree, 22 domande
// ─────────────────────────────────────────────────────────────────────────────
const AREAS = [
  {
    id: "patrimoniale",
    label: "Solidità Patrimoniale",
    questions: [
      {
        id: "p1",
        text: "Qual è il patrimonio netto attuale della tua startup?",
        hint: "Il patrimonio netto si trova nel passivo dello Stato Patrimoniale. Include: Capitale sociale + Riserve (sovrapprezzo, legale, straordinarie) + Utili/perdite portati a nuovo + Utile/perdita d'esercizio.",
        options: [
          { label: "Negativo o sotto i 5.000 €", inv: 0, bank: 0 },
          { label: "Tra 5.000 € e 30.000 €", inv: 2, bank: 3 },
          { label: "Tra 30.000 € e 100.000 €", inv: 5, bank: 8 },
          { label: "Oltre 100.000 €", inv: 8, bank: 12 },
        ],
      },
      {
        id: "p2",
        text: "Hai effettuato operazioni di work for equity o apporti in natura valorizzati a bilancio?",
        options: [
          { label: "No, nessuna operazione di questo tipo", inv: 0, bank: 0 },
          { label: "Sì, ma non correttamente contabilizzate", inv: 1, bank: 1 },
          { label: "Sì, contabilizzate ma senza perizia o valutazione formale", inv: 3, bank: 4 },
          { label: "Sì, con documentazione completa e valutazione formale", inv: 6, bank: 8 },
        ],
      },
      {
        id: "p3",
        text: "La tua startup ha riserve disponibili (es. riserva sovrapprezzo quote)?",
        hint: "Le riserve disponibili sono fondi accantonati che possono essere usati liberamente (es. per coprire perdite o aumentare capitale). Le trovi nel patrimonio netto: Riserva sovrapprezzo quote, Riserva straordinaria, Altre riserve. Non sono riserve disponibili: Riserva legale e Riserve vincolate.",
        options: [
          { label: "No, nessuna riserva", inv: 0, bank: 0 },
          { label: "Riserve minime (sotto 10.000 €)", inv: 2, bank: 3 },
          { label: "Riserve significative (10.000–50.000 €)", inv: 4, bank: 6 },
          { label: "Riserve consistenti (oltre 50.000 €)", inv: 6, bank: 9 },
        ],
      },
      {
        id: "p4",
        text: "Il rapporto debiti finanziari / patrimonio netto (D/E ratio) è:",
        hint: "I debiti finanziari includono: finanziamenti bancari, prestiti soci, obbligazioni, leasing finanziari. Li trovi nel passivo dello Stato Patrimoniale alla voce 'Debiti verso banche' e 'Debiti verso altri finanziatori'. Non considerare debiti commerciali (fornitori) o debiti tributari.",
        options: [
          { label: "Non lo conosco o non l'ho mai calcolato", inv: 0, bank: 0 },
          { label: "Superiore a 3:1 (troppo indebitata)", inv: 1, bank: 1 },
          { label: "Tra 1:1 e 3:1 (gestibile)", inv: 4, bank: 5 },
          { label: "Sotto 1:1 (struttura solida)", inv: 6, bank: 8 },
        ],
      },
    ],
  },
  {
    id: "tesoreria",
    label: "Tesoreria e Liquidità",
    
    questions: [
      {
        id: "t1",
        text: "Conosci con precisione il tuo runway attuale (mesi di cassa disponibile)?",
        hint: "Il runway indica per quanti mesi la tua startup può operare con la liquidità attuale, senza nuovi incassi. Si calcola: Liquidità disponibile ÷ Spese mensili medie = Mesi di runway. Es: 30.000€ in cassa ÷ 5.000€/mese = 6 mesi di runway.",
        options: [
          { label: "No, non ho una stima precisa", inv: 0, bank: 0 },
          { label: "Ho un'idea approssimativa (±3 mesi)", inv: 2, bank: 2 },
          { label: "Sì, lo aggiorno mensilmente", inv: 5, bank: 6 },
          { label: "Sì, aggiornato ogni settimana con piano a 13 settimane", inv: 8, bank: 10 },
        ],
      },
      {
        id: "t2",
        text: "Qual è il tuo runway attuale?",
        options: [
          { label: "Meno di 3 mesi", inv: 0, bank: 0 },
          { label: "3–6 mesi", inv: 2, bank: 3 },
          { label: "6–12 mesi", inv: 5, bank: 7 },
          { label: "Oltre 12 mesi", inv: 8, bank: 10 },
        ],
      },
      {
        id: "t3",
        text: "Come gestisci la previsione di cassa?",
        options: [
          { label: "Non ho un sistema strutturato", inv: 0, bank: 0 },
          { label: "Verifico il saldo bancario periodicamente", inv: 1, bank: 1 },
          { label: "Ho un foglio Excel con entrate/uscite mensili", inv: 4, bank: 5 },
          { label: "Ho un piano di tesoreria a 13 settimane aggiornato", inv: 7, bank: 9 },
        ],
      },
      {
        id: "t4",
        text: "Hai mai avuto difficoltà a pagare fornitori o collaboratori nei tempi previsti?",
        options: [
          { label: "Sì, frequentemente", inv: 0, bank: 0 },
          { label: "Sì, qualche volta", inv: 2, bank: 2 },
          { label: "Raramente, solo in situazioni straordinarie", inv: 5, bank: 6 },
          { label: "Mai — la liquidità è sempre gestita con anticipo", inv: 7, bank: 9 },
        ],
      },
    ],
  },
  {
    id: "trazione",
    label: "Trazione e Revenue",
    
    questions: [
      {
        id: "r1",
        text: "Qual è la situazione del tuo fatturato?",
        options: [
          { label: "Nessun ricavo ancora (pre-revenue)", inv: 1, bank: 0 },
          { label: "Primi ricavi sporadici (sotto 10.000 €/anno)", inv: 3, bank: 2 },
          { label: "Ricavi ricorrenti o in crescita (10.000–100.000 €/anno)", inv: 7, bank: 5 },
          { label: "Ricavi significativi (oltre 100.000 €/anno) e in crescita", inv: 10, bank: 8 },
        ],
      },
      {
        id: "r2",
        text: "Come sta crescendo il tuo fatturato (o la tua base clienti)?",
        options: [
          { label: "Non ho ancora dati di crescita", inv: 1, bank: 0 },
          { label: "Crescita inferiore al 20% annuo", inv: 3, bank: 3 },
          { label: "Crescita tra il 20% e il 100% annuo", inv: 7, bank: 5 },
          { label: "Crescita oltre il 100% annuo (o forte pipeline)", inv: 10, bank: 6 },
        ],
      },
      {
        id: "r3",
        text: "Hai clienti paganti o contratti firmati?",
        options: [
          { label: "No, sono ancora in fase di sviluppo prodotto", inv: 1, bank: 0 },
          { label: "Ho lettere di intento (LOI), ma non contratti", inv: 3, bank: 1 },
          { label: "Ho i primi clienti paganti (1–10 clienti)", inv: 7, bank: 4 },
          { label: "Ho una base clienti consolidata con rinnovi", inv: 10, bank: 7 },
        ],
      },
      {
        id: "r4",
        text: "Conosci il tuo unit economics (LTV, CAC, margine per cliente)?",
        hint: "LTV (Lifetime Value) = ricavo totale medio generato da un cliente nel tempo. CAC (Customer Acquisition Cost) = costo medio per acquisire un nuovo cliente (marketing + vendite ÷ nuovi clienti). Margine per cliente = ricavo per cliente − costi diretti per servirlo. Un business sano ha LTV > 3× CAC.",
        options: [
          { label: "No, non ho ancora calcolato questi indicatori", inv: 0, bank: 0 },
          { label: "Ho stime approssimative non validate", inv: 3, bank: 2 },
          { label: "Ho dati basati sui primi clienti reali", inv: 7, bank: 4 },
          { label: "Ho dati consolidati e un modello validato", inv: 10, bank: 6 },
        ],
      },
    ],
  },
  {
    id: "team",
    label: "Team e Governance",
    
    questions: [
      {
        id: "g1",
        text: "Come è composto il team fondatore?",
        options: [
          { label: "Solo founder, nessun co-founder", inv: 2, bank: 2 },
          { label: "2–3 co-founder con competenze simili", inv: 4, bank: 4 },
          { label: "2–3 co-founder con competenze complementari (tech + business)", inv: 8, bank: 6 },
          { label: "Team completo con esperienze comprovate nel settore", inv: 10, bank: 8 },
        ],
      },
      {
        id: "g2",
        text: "Hai advisor o un advisory board strutturato?",
        options: [
          { label: "No", inv: 0, bank: 1 },
          { label: "Ho contatti informali con esperti del settore", inv: 3, bank: 2 },
          { label: "Ho 1–2 advisor formali con accordo scritto", inv: 6, bank: 4 },
          { label: "Ho un advisory board attivo con profili complementari", inv: 9, bank: 6 },
        ],
      },
      {
        id: "g3",
        text: "La governance societaria è strutturata (statuto, patti parasociali, CDA)?",
        options: [
          { label: "No, gestiamo tutto informalmente", inv: 1, bank: 1 },
          { label: "Abbiamo uno statuto base, nient'altro", inv: 3, bank: 4 },
          { label: "Abbiamo statuto e patti parasociali", inv: 6, bank: 7 },
          { label: "Governance completa: statuto, patti, CDA operativo, deleghe chiare", inv: 9, bank: 9 },
        ],
      },
    ],
  },
  {
    id: "documentazione",
    label: "Documentazione",
    
    questions: [
      {
        id: "d1",
        text: "Hai un business plan aggiornato con proiezioni finanziarie a 3 anni?",
        options: [
          { label: "No", inv: 0, bank: 0 },
          { label: "Ho un piano qualitativo senza proiezioni numeriche", inv: 2, bank: 1 },
          { label: "Ho proiezioni finanziarie ma non verificate da terzi", inv: 5, bank: 4 },
          { label: "Ho un business plan completo, aggiornato e validato", inv: 9, bank: 7 },
        ],
      },
      {
        id: "d2",
        text: "I tuoi bilanci sono in ordine e regolarmente depositati?",
        options: [
          { label: "Siamo appena costituiti, primo bilancio non ancora dovuto", inv: 4, bank: 5 },
          { label: "Abbiamo ritardi o irregolarità nei depositi", inv: 0, bank: 0 },
          { label: "Bilanci depositati ma non analizzati internamente", inv: 4, bank: 6 },
          { label: "Bilanci depositati, analizzati e con nota integrativa dettagliata", inv: 8, bank: 10 },
        ],
      },
      {
        id: "d3",
        text: "Hai una data room (raccolta documenti per investitori/banche)?",
        options: [
          { label: "No, non so cosa sia una data room", inv: 0, bank: 0 },
          { label: "Ho documenti sparsi ma non organizzati", inv: 2, bank: 2 },
          { label: "Ho una cartella digitale con i principali documenti", inv: 5, bank: 6 },
          { label: "Ho una data room strutturata e aggiornata", inv: 9, bank: 10 },
        ],
      },
      {
        id: "d4",
        text: "Hai una valutazione premoney formalizzata o certificata?",
        options: [
          { label: "No, mai calcolata", inv: 0, bank: 0 },
          { label: "Ho fatto una stima interna", inv: 2, bank: 1 },
          { label: "Ho una valutazione esterna non certificata", inv: 5, bank: 3 },
          { label: "Ho una valutazione certificata da professionista indipendente", inv: 9, bank: 6 },
        ],
      },
    ],
  },
  {
    id: "strategico",
    label: "Posizionamento Strategico",
    
    questions: [
      {
        id: "s1",
        text: "Hai validato la dimensione del mercato (TAM/SAM/SOM)?",
        options: [
          { label: "No, ho solo intuizioni sul mercato", inv: 0, bank: 0 },
          { label: "Ho stime informali senza fonti", inv: 2, bank: 1 },
          { label: "Ho ricerche di mercato con fonti citate", inv: 6, bank: 3 },
          { label: "Ho un'analisi strutturata con dati terzi verificabili", inv: 9, bank: 5 },
        ],
      },
      {
        id: "s2",
        text: "Il tuo vantaggio competitivo è:",
        options: [
          { label: "Non ancora definito chiaramente", inv: 0, bank: 0 },
          { label: "Basato principalmente sul prezzo", inv: 2, bank: 2 },
          { label: "Basato su prodotto/servizio differenziato", inv: 6, bank: 4 },
          { label: "Difendibile: tecnologia proprietaria, network effect o brevetto", inv: 10, bank: 6 },
        ],
      },
      {
        id: "s3",
        text: "Il tuo modello di business è scalabile senza proporzionale aumento dei costi?",
        options: [
          { label: "No, ogni nuovo cliente richiede lo stesso sforzo manuale", inv: 1, bank: 2 },
          { label: "Parzialmente scalabile (alcune aree automatizzate)", inv: 4, bank: 4 },
          { label: "Scalabile con investimenti limitati", inv: 7, bank: 5 },
          { label: "Altamente scalabile (es. SaaS, marketplace, platform)", inv: 10, bank: 6 },
        ],
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const MAX_INV = AREAS.reduce((t, a) => t + a.questions.reduce((s, q) => s + Math.max(...q.options.map(o => o.inv)), 0), 0);
const MAX_BANK = AREAS.reduce((t, a) => t + a.questions.reduce((s, q) => s + Math.max(...q.options.map(o => o.bank)), 0), 0);
const TOTAL_Q = AREAS.reduce((t, a) => t + a.questions.length, 0);

function calcScores(answers) {
  let inv = 0, bank = 0;
  for (const area of AREAS) {
    for (const q of area.questions) {
      if (answers[q.id] !== undefined) {
        inv += q.options[answers[q.id]].inv;
        bank += q.options[answers[q.id]].bank;
      }
    }
  }
  return {
    invScore: Math.round((inv / MAX_INV) * 100),
    bankScore: Math.round((bank / MAX_BANK) * 100),
  };
}

function getTag(invScore, bankScore) {
  if (invScore >= 75) return "investor-ready";
  if (bankScore >= 75) return "bank-ready";
  if (invScore >= 55 || bankScore >= 55) return "in-preparazione";
  return "early-stage";
}

function getSequence(invScore, bankScore) {
  if (invScore > bankScore + 10) return "investor";
  if (bankScore > invScore + 10) return "bank";
  return "mixed";
}

function getBand(pct) {
  if (pct < 30) return { label: "Non ancora pronto", color: "#ef4444", bg: "#fef2f2", advice: "Hai lacune strutturali da colmare prima di presentarti." };
  if (pct < 55) return { label: "In preparazione", color: "#F57C00", bg: "#FFF3E0", advice: "Sei sulla strada giusta, ma ci sono aree critiche da rafforzare." };
  if (pct < 75) return { label: "Quasi pronto", color: "#3b82f6", bg: "#eff6ff", advice: "Mancano pochi passaggi per presentarti con credibilità." };
  return { label: "Pronto", color: "#10b981", bg: "#f0fdf4", advice: "Il tuo profilo è solido. Massimizza la presentazione." };
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function ScoreArc({ pct, color, label, band }) {
  const r = 50, circ = 2 * Math.PI * r;
  const fill = (pct / 100) * circ;
  return (
    <div className="flex flex-col items-center gap-2">
      <svg width={124} height={124} viewBox="0 0 124 124">
        <circle cx={62} cy={62} r={r} fill="none" stroke="#e2e8f0" strokeWidth={9} />
        <circle cx={62} cy={62} r={r} fill="none" stroke={color} strokeWidth={9}
          strokeDasharray={`${fill} ${circ}`} strokeLinecap="round"
          transform="rotate(-90 62 62)"
          style={{ transition: "stroke-dasharray 1.4s cubic-bezier(0.16,1,0.3,1)" }} />
        <text x={62} y={58} textAnchor="middle" fontSize={27} fontWeight={800}
          className="font-display" fill="#101010">{pct}</text>
        <text x={62} y={74} textAnchor="middle" fontSize={11}
          fill="#6B7280">/100</text>
      </svg>
      <div className="text-center">
        <div className="font-display font-bold text-sm text-gray-800">{label}</div>
        <div className="inline-block rounded-full px-3 py-1 text-xs font-semibold mt-1"
          style={{ background: band.bg, color: band.color }}>
          {band.label}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGES
// ─────────────────────────────────────────────────────────────────────────────

function PageIntro({ onStart }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-10 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #0a0a0a 0%, #101010 50%, #0a0a0a 100%)" }}>
      {/* Background glows */}
      <div className="absolute -top-44 -right-44 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(245,124,0,0.18) 0%, transparent 65%)" }} />
      <div className="absolute -bottom-20 -left-20 w-[360px] h-[360px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(245,124,0,0.10) 0%, transparent 65%)" }} />
      <div className="absolute top-1/3 left-1/4 w-[200px] h-[200px] rounded-full pointer-events-none animate-float"
        style={{ background: "radial-gradient(circle, rgba(245,124,0,0.06) 0%, transparent 70%)" }} />

      <div className="animate-fade max-w-2xl w-full text-center">
        {/* Logo */}
        <img src="/logo.png" alt="Creazioneimpresa" className="h-28 mx-auto mb-10" data-testid="logo" />

        {/* Badge */}
        <div className="inline-flex items-center gap-2 glass rounded-full px-5 py-2 mb-8">
          <Sparkles className="w-4 h-4" style={{ color: "#F57C00" }} />
          <span className="text-sm font-medium tracking-wide uppercase font-display" style={{ color: "#FFB74D" }}>
            by Creazioneimpresa
          </span>
        </div>

        <h1 className="font-display text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
          Startup<br />
          <span className="bg-gradient-to-r from-[#F57C00] to-[#FFB74D] bg-clip-text text-transparent">
            Score
          </span>
        </h1>

        <p className="text-gray-300 text-xl leading-relaxed mb-4 font-light max-w-lg mx-auto">
          Scopri in <strong className="text-white font-semibold">10 minuti</strong> se sei pronto
          per un <strong className="font-semibold" style={{ color: "#FFB74D" }}>investitore</strong> o
          per una <strong className="font-semibold" style={{ color: "#34d399" }}>banca</strong>.
        </p>
        <p className="text-gray-500 text-sm mb-12">
          {TOTAL_Q} domande · 6 aree · Doppio score personalizzato · Gratuito
        </p>

        {/* Areas with icons */}
        <div className="flex flex-wrap gap-3 justify-center mb-12">
          {AREAS.map(a => {
            const IconComponent = AREA_ICONS[a.id];
            return (
              <div key={a.id} className="glass flex items-center gap-2 rounded-xl px-4 py-2.5 transition-all hover:scale-105">
                <IconComponent className="w-4 h-4" style={{ color: "#F57C00" }} />
                <span className="text-gray-300 text-sm font-medium">{a.label}</span>
              </div>
            );
          })}
        </div>

        <button onClick={onStart} data-testid="start-quiz-btn"
          className="font-display btn-primary text-white border-none rounded-2xl px-14 py-5 text-lg font-bold cursor-pointer inline-flex items-center gap-3"
          style={{ background: "linear-gradient(135deg, #F57C00, #E65100)", boxShadow: "0 0 50px rgba(245,124,0,0.4)" }}>
          Inizia la valutazione
          <ArrowRight className="w-5 h-5" />
        </button>
        <p className="text-gray-500 text-xs mt-5">Nessuna registrazione richiesta · Risultati immediati</p>
      </div>
    </div>
  );
}

function PageQuiz({ answers, onAnswer, onComplete }) {
  const [areaIdx, setAreaIdx] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [animating, setAnimating] = useState(false);

  const area = AREAS[areaIdx];
  const q = area.questions[qIdx];
  const answered = Object.keys(answers).length;
  const progress = (answered / TOTAL_Q) * 100;
  const isLast = areaIdx === AREAS.length - 1 && qIdx === area.questions.length - 1;
  const IconComponent = AREA_ICONS[area.id];

  function next() {
    if (selected === null) return;
    const newAnswers = { ...answers, [q.id]: selected };
    onAnswer(newAnswers);
    setAnimating(true);
    setTimeout(() => {
      setSelected(null);
      setAnimating(false);
      if (qIdx < area.questions.length - 1) {
        setQIdx(i => i + 1);
      } else if (areaIdx < AREAS.length - 1) {
        setAreaIdx(i => i + 1);
        setQIdx(0);
      } else {
        onComplete(newAnswers);
      }
    }, 280);
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Topbar */}
      <div className="bg-white border-b border-gray-100 px-6 py-4 flex items-center gap-5 sticky top-0 z-10 shadow-sm">
        <img src="/logo.png" alt="Creazioneimpresa" className="h-9" />
        <span className="font-display font-bold text-base whitespace-nowrap" style={{ color: "#101010" }}>
          Startup Score
        </span>
        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500"
            style={{ width: `${progress}%`, background: "linear-gradient(90deg, #F57C00, #10b981)" }} />
        </div>
        <span className="text-gray-500 text-sm font-medium whitespace-nowrap">{answered}/{TOTAL_Q}</span>
      </div>

      <div className="flex-1 flex flex-col items-center p-8 max-w-2xl mx-auto w-full">
        {/* Area header */}
        <div className={`w-full mb-6 ${animating ? '' : 'animate-slide'}`} key={`hdr-${areaIdx}`}>
          <div className="flex items-center gap-4 mb-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "#FFF3E0" }}>
              <IconComponent className="w-6 h-6" style={{ color: "#F57C00" }} />
            </div>
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wider font-semibold">
                Area {areaIdx + 1} di {AREAS.length}
              </p>
              <p className="font-display font-bold text-xl" style={{ color: "#101010" }}>{area.label}</p>
            </div>
          </div>
          <div className="flex gap-1.5">
            {area.questions.map((_, i) => (
              <div key={i} className="h-1.5 flex-1 rounded-full transition-colors duration-300"
                style={{ background: i < qIdx ? "#F57C00" : i === qIdx ? "#FFB74D" : "#e2e8f0" }} />
            ))}
          </div>
        </div>

        {/* Question */}
        <div className={`w-full ${animating ? '' : 'animate-slide'}`} key={`q-${areaIdx}-${qIdx}`}>
          <div className="bg-white border border-gray-100 rounded-2xl p-7 mb-5 shadow-sm">
            <p className="text-gray-400 text-xs mb-3 font-semibold uppercase tracking-wide">
              Domanda {qIdx + 1} di {area.questions.length}
            </p>
            <h2 className="font-display font-bold text-xl leading-snug" style={{ color: "#101010" }}>
              {q.text}
            </h2>
            {q.hint && (
              <div className="mt-4 p-4 rounded-xl" style={{ background: "#FFF3E0", border: "1px solid #FFE0B2" }}>
                <div className="flex items-start gap-2">
                  <span className="text-sm mt-0.5" style={{ color: "#F57C00" }}>💡</span>
                  <p className="text-sm leading-relaxed" style={{ color: "#7C4700" }}>
                    {q.hint}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Options */}
          <div className="flex flex-col gap-3">
            {q.options.map((opt, i) => {
              const sel = selected === i;
              return (
                <div key={i} data-testid={`option-${i}`}
                  className="option-card rounded-xl p-5 flex items-center gap-4"
                  onClick={() => setSelected(i)}
                  style={{
                    background: sel ? "linear-gradient(135deg, #FFF3E0, #FFE0B2)" : "#fff",
                    border: sel ? "2px solid #F57C00" : "2px solid #e5e7eb",
                    boxShadow: sel ? "0 0 0 4px rgba(245,124,0,0.1)" : "0 1px 3px rgba(0,0,0,0.04)",
                  }}>
                  <div className="w-6 h-6 rounded-full flex-shrink-0 transition-all duration-150 flex items-center justify-center"
                    style={{
                      border: sel ? "none" : "2px solid #d1d5db",
                      background: sel ? "#F57C00" : "transparent",
                    }}>
                    {sel && <CheckCircle2 className="w-6 h-6 text-white" />}
                  </div>
                  <span className="text-base leading-relaxed"
                    style={{ color: sel ? "#C2410C" : "#374151", fontWeight: sel ? 600 : 400 }}>
                    {opt.label}
                  </span>
                </div>
              );
            })}
          </div>

          <button onClick={next} disabled={selected === null} data-testid="next-btn"
            className="w-full mt-6 py-4 rounded-xl font-display font-bold text-base btn-primary border-none inline-flex items-center justify-center gap-2"
            style={{
              background: selected !== null ? "linear-gradient(135deg, #F57C00, #E65100)" : "#e2e8f0",
              color: selected !== null ? "#fff" : "#94a3b8",
              cursor: selected !== null ? "pointer" : "not-allowed",
              boxShadow: selected !== null ? "0 4px 18px rgba(245,124,0,0.25)" : "none"
            }}>
            {isLast ? "Visualizza i risultati" : "Avanti"}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function PageLead({ onSubmit, loading }) {
  const [form, setForm] = useState({ name: "", email: "", startup: "" });
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <div className="min-h-screen flex items-center justify-center p-10 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #0a0a0a 0%, #101010 50%, #0a0a0a 100%)" }}>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(245,124,0,0.15) 0%, transparent 65%)" }} />

      <div className="animate-fade max-w-md w-full text-center">
        <div className="w-20 h-20 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ background: "rgba(245,124,0,0.15)" }}>
          <Award className="w-10 h-10" style={{ color: "#F57C00" }} />
        </div>
        <h2 className="font-display text-white text-3xl font-extrabold mb-4">
          Ci sei quasi!
        </h2>
        <p className="text-gray-400 text-lg mb-8 leading-relaxed">
          Il tuo Startup Score è pronto.<br />
          Inserisci i dati per sbloccare il report completo.
        </p>

        <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="flex flex-col gap-4">
          {[
            { k: "name", p: "Il tuo nome", t: "text" },
            { k: "email", p: "Email aziendale", t: "email" },
            { k: "startup", p: "Nome della startup", t: "text" },
          ].map(f => (
            <input key={f.k} type={f.t} placeholder={f.p} required
              data-testid={`input-${f.k}`}
              value={form[f.k]} onChange={set(f.k)}
              className="w-full py-4 px-5 rounded-xl text-base text-white font-medium"
              style={{
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.08)",
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }} />
          ))}

          <button type="submit" disabled={loading} data-testid="submit-btn"
            className="font-display btn-primary py-4 rounded-xl text-base font-bold border-none mt-3 inline-flex items-center justify-center gap-2"
            style={{
              background: loading ? "rgba(255,255,255,0.1)" : "linear-gradient(135deg, #F57C00, #10b981)",
              color: loading ? "#64748b" : "#fff",
              cursor: loading ? "not-allowed" : "pointer",
              boxShadow: loading ? "none" : "0 4px 28px rgba(245,124,0,0.3)"
            }}>
            {loading ? "Elaborazione..." : "Scopri il tuo Score"}
            {!loading && <ArrowRight className="w-5 h-5" />}
          </button>
        </form>

        <p className="text-gray-500 text-sm mt-5">
          Nessuno spam · Dati non condivisi con terzi
        </p>
      </div>
    </div>
  );
}

function PageResult({ lead, invScore, bankScore, answers, onRestart }) {
  const invBand = getBand(invScore);
  const bankBand = getBand(bankScore);

  const areaScores = AREAS.map(area => {
    const maxI = area.questions.reduce((s, q) => s + Math.max(...q.options.map(o => o.inv)), 0);
    const maxB = area.questions.reduce((s, q) => s + Math.max(...q.options.map(o => o.bank)), 0);
    const gotI = area.questions.reduce((s, q) => answers[q.id] !== undefined ? s + q.options[answers[q.id]].inv : s, 0);
    const gotB = area.questions.reduce((s, q) => answers[q.id] !== undefined ? s + q.options[answers[q.id]].bank : s, 0);
    return {
      id: area.id,
      label: area.label,
      invPct: Math.round((gotI / maxI) * 100),
      bankPct: Math.round((gotB / maxB) * 100),
    };
  });

  const weakInv = [...areaScores].sort((a, b) => a.invPct - b.invPct).slice(0, 2);
  const weakBank = [...areaScores].sort((a, b) => a.bankPct - b.bankPct).slice(0, 2);
  const tag = getTag(invScore, bankScore);
  const sequence = getSequence(invScore, bankScore);

  const tagColors = {
    "investor-ready": { bg: "#FFF3E0", color: "#F57C00", label: "Investor Ready" },
    "bank-ready": { bg: "#f0fdf4", color: "#10b981", label: "Bank Ready" },
    "in-preparazione": { bg: "#fffbeb", color: "#f59e0b", label: "In Preparazione" },
    "early-stage": { bg: "#fef2f2", color: "#ef4444", label: "Early Stage" },
  };
  const tc = tagColors[tag];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="result-page">
      {/* Header */}
      <div className="px-5 py-8 text-center" style={{ background: "linear-gradient(135deg, #0a0a0a 0%, #101010 100%)" }}>
        <img src="/logo.png" alt="Creazioneimpresa" className="h-12 mx-auto mb-4" />
        <span className="font-display text-gray-500 text-xs tracking-wider uppercase">
          Startup Score
        </span>
        <h1 className="font-display animate-fade text-white text-3xl md:text-4xl font-extrabold mt-3">
          Il tuo report, {lead.name.split(" ")[0]}
        </h1>
        <div className="flex items-center justify-center gap-3 mt-2">
          <span className="text-gray-500 text-sm">{lead.startup}</span>
          <span className="inline-block rounded-full px-3 py-0.5 text-xs font-semibold"
            style={{ background: tc.bg, color: tc.color }}>
            {tc.label}
          </span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-7">
        {/* Score circles */}
        <div className="animate-fade bg-white rounded-2xl p-8 shadow-sm border border-gray-200 mb-4 flex gap-6 justify-center flex-wrap items-center">
          <ScoreArc pct={invScore} color="#F57C00" label="Investor Readiness" band={invBand} />
          <div className="w-px h-24 bg-gray-200 hidden sm:block" />
          <ScoreArc pct={bankScore} color="#10b981" label="Bank Readiness" band={bankBand} />
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {[
            { title: "Investor Readiness", pct: invScore, band: invBand, color: "#F57C00", bg: "#FFF3E0", border: "#FFCC80", Icon: Briefcase },
            { title: "Bank Readiness", pct: bankScore, band: bankBand, color: "#10b981", bg: "#f0fdf4", border: "#a7f3d0", Icon: Landmark },
          ].map(item => (
            <div key={item.title} className="rounded-xl p-5" style={{ background: item.bg, border: `1px solid ${item.border}` }}>
              <item.Icon className="w-7 h-7 mb-3" style={{ color: item.color }} />
              <div className="font-display font-bold text-sm mb-1" style={{ color: "#101010" }}>{item.title}</div>
              <div className="font-display font-bold text-3xl mb-1" style={{ color: item.color }}>
                {item.pct}<span className="text-sm font-normal text-gray-400">/100</span>
              </div>
              <p className="text-gray-600 text-xs leading-relaxed">{item.band.advice}</p>
            </div>
          ))}
        </div>

        {/* Area breakdown */}
        <div className="animate-fade bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-4">
          <h3 className="font-display font-bold text-lg mb-5" style={{ color: "#101010" }}>
            Analisi per area
          </h3>
          <div className="flex flex-col gap-4">
            {areaScores.map((a, i) => {
              const AreaIcon = AREA_ICONS[a.id];
              return (
                <div key={i}>
                  <div className="flex justify-between mb-1.5 items-center">
                    <span className="flex items-center gap-2 font-medium text-gray-700 text-sm">
                      <AreaIcon className="w-4 h-4" style={{ color: "#F57C00" }} />{a.label}
                    </span>
                    <span className="text-xs">
                      <span className="font-semibold" style={{ color: "#F57C00" }}>{a.invPct}%</span>
                      <span className="mx-1 text-gray-300">|</span>
                      <span className="font-semibold text-green-500">{a.bankPct}%</span>
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-lg overflow-hidden relative">
                    <div className="absolute left-0 top-0 h-full rounded-lg"
                      style={{ width: `${a.invPct}%`, background: "rgba(245,124,0,0.25)" }} />
                    <div className="absolute left-0 top-0 h-full rounded-lg"
                      style={{ width: `${Math.min(a.invPct, a.bankPct)}%`, background: "linear-gradient(90deg, #F57C00, #10b981)" }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-4 pt-4 border-t border-gray-100">
            {[["#F57C00", "Investor"], ["#10b981", "Banca"]].map(([c, l]) => (
              <span key={l} className="text-xs text-gray-500 flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: c }} />{l}
              </span>
            ))}
          </div>
        </div>

        {/* Priorities */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {[
            { title: "Priorità per gli investitori", items: weakInv, color: "#F57C00", bg: "#FFF3E0", border: "#FFCC80", inv: true },
            { title: "Priorità per la banca", items: weakBank, color: "#10b981", bg: "#f0fdf4", border: "#a7f3d0", inv: false },
          ].map(block => (
            <div key={block.title} className="rounded-xl p-5" style={{ background: block.bg, border: `1px solid ${block.border}` }}>
              <h4 className="font-display font-bold text-sm mb-3" style={{ color: "#101010" }}>{block.title}</h4>
              <p className="text-xs text-gray-500 mb-3">Aree con maggiore margine di miglioramento:</p>
              {block.items.map((a, i) => {
                const AreaIcon = AREA_ICONS[a.id];
                return (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <AreaIcon className="w-5 h-5" style={{ color: block.color }} />
                    <div>
                      <div className="font-semibold text-xs" style={{ color: "#101010" }}>{a.label}</div>
                      <div className="text-xs font-medium" style={{ color: block.color }}>
                        Score: {block.inv ? a.invPct : a.bankPct}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Email sequence info */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4 flex items-center gap-3">
          <Mail className="w-6 h-6" style={{ color: "#F57C00" }} />
          <div>
            <p className="text-sm font-medium" style={{ color: "#101010" }}>
              Riceverai via email una sequenza personalizzata
            </p>
            <p className="text-xs text-gray-400">
              {sequence === "investor" && "Contenuti mirati per prepararti a incontrare un investitore"}
              {sequence === "bank" && "Contenuti mirati per prepararti all'accesso al credito bancario"}
              {sequence === "mixed" && "Contenuti bilanciati su investitori e accesso al credito"}
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="rounded-2xl p-8 text-center" style={{ background: "linear-gradient(135deg, #101010, #0a0a0a)" }}>
          <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: "rgba(245,124,0,0.15)" }}>
            <Rocket className="w-8 h-8" style={{ color: "#F57C00" }} />
          </div>
          <h3 className="font-display text-white text-xl font-extrabold mb-3">
            Vuoi colmare queste lacune?
          </h3>
          <p className="text-gray-400 text-sm mb-6 leading-relaxed max-w-sm mx-auto">
            Il team di Creazioneimpresa lavora con startup early stage per trasformare
            esattamente queste aree in punti di forza concreti.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <a href="https://creazioneimpresa.net/consulenza" target="_blank" rel="noreferrer"
              data-testid="cta-consulenza"
              className="font-display btn-primary text-white border-none rounded-xl px-6 py-3.5 text-sm font-bold no-underline inline-flex items-center gap-2"
              style={{ background: "linear-gradient(135deg, #F57C00, #E65100)", boxShadow: "0 4px 18px rgba(245,124,0,0.3)" }}>
              Prenota una consulenza gratuita
              <ArrowRight className="w-4 h-4" />
            </a>
            <button onClick={onRestart} data-testid="restart-btn"
              className="font-display text-gray-400 bg-transparent rounded-xl px-6 py-3.5 text-sm font-semibold"
              style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
              Rifai il test
            </button>
          </div>
          <p className="text-gray-500 text-xs mt-4">
            Riceverai il report completo via email · {lead.email}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD (Firebase Auth + Firestore)
// ─────────────────────────────────────────────────────────────────────────────

function DashboardLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch {
      setErr("Accesso non riuscito. Verifica email e password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-8"
      style={{ background: "linear-gradient(135deg, #0a0a0a 0%, #101010 100%)" }}>
      <img src="/logo.png" alt="Creazioneimpresa" className="h-16 mb-8" />
      <div className="w-full max-w-sm rounded-2xl p-8 border border-white/10"
        style={{ background: "rgba(255,255,255,0.05)" }}>
        <h1 className="font-display text-white text-xl font-bold mb-1">Dashboard admin</h1>
        <p className="text-gray-500 text-sm mb-6">Accedi con l&apos;account Firebase (Email/Password).</p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <input type="email" required autoComplete="email" placeholder="Email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full py-3 px-4 rounded-xl text-white text-sm border border-white/15 bg-white/5" />
          <input type="password" required autoComplete="current-password" placeholder="Password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full py-3 px-4 rounded-xl text-white text-sm border border-white/15 bg-white/5" />
          {err && <p className="text-red-400 text-sm">{err}</p>}
          <button type="submit" disabled={busy}
            className="font-display font-bold py-3 rounded-xl text-white border-none"
            style={{ background: busy ? "#444" : "linear-gradient(135deg, #F57C00, #E65100)" }}>
            {busy ? "…" : "Accedi"}
          </button>
        </form>
        <Link to="/" className="block text-center text-sm mt-6 no-underline" style={{ color: "#FFB74D" }}>
          ← Torna al quiz
        </Link>
      </div>
    </div>
  );
}

function Dashboard() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      setStats(null);
      setLeads([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await fetchLeadsOrdered();
        if (cancelled) return;
        setLeads(list);
        setStats(computeDashboardStats(list));
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setLeads([]);
          setStats(computeDashboardStats([]));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const filteredLeads = leads.filter(l =>
    !filter || l.tag === filter
  );

  const tagColors = {
    "investor-ready": { bg: "#FFF3E0", color: "#F57C00", label: "Investor Ready" },
    "bank-ready": { bg: "#f0fdf4", color: "#10b981", label: "Bank Ready" },
    "in-preparazione": { bg: "#fffbeb", color: "#f59e0b", label: "In Preparazione" },
    "early-stage": { bg: "#fef2f2", color: "#ef4444", label: "Early Stage" },
  };

  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#101010" }}>
        <div className="text-white text-xl font-display">Caricamento...</div>
      </div>
    );
  }

  if (!user) {
    return <DashboardLogin />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#101010" }}>
        <div className="text-white text-xl font-display">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="px-6 py-5 flex items-center justify-between" style={{ background: "linear-gradient(135deg, #0a0a0a 0%, #101010 100%)" }}>
        <div className="flex items-center gap-4">
          <img src="/logo.png" alt="Creazioneimpresa" className="h-10" />
          <div>
            <h1 className="font-display text-white text-xl font-bold">Dashboard Admin</h1>
            <p className="text-gray-400 text-xs">Gestione lead Startup Score</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => signOut(auth)}
            className="text-sm px-4 py-2 rounded-lg font-medium border border-white/20 text-gray-300 bg-transparent cursor-pointer">
            Esci
          </button>
          <Link to="/" data-testid="back-to-quiz"
            className="text-sm px-4 py-2 rounded-lg font-medium no-underline inline-flex items-center gap-2"
            style={{ background: "rgba(245,124,0,0.15)", color: "#FFB74D" }}>
            <ChevronRight className="w-4 h-4 rotate-180" />
            Torna al Quiz
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Lead Totali", value: stats?.total_leads || 0, Icon: Users, color: "#F57C00" },
            { label: "Media Investor Score", value: `${stats?.avg_inv_score || 0}%`, Icon: Briefcase, color: "#F57C00" },
            { label: "Media Bank Score", value: `${stats?.avg_bank_score || 0}%`, Icon: Landmark, color: "#10b981" },
            { label: "Lead Questo Mese", value: stats?.leads_this_month || 0, Icon: BarChart3, color: "#3b82f6" },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <stat.Icon className="w-6 h-6 mb-2" style={{ color: stat.color }} />
              <div className="text-xs text-gray-500 mb-1">{stat.label}</div>
              <div className="font-display font-bold text-2xl" style={{ color: stat.color }}>{stat.value}</div>
            </div>
          ))}
        </div>

        {/* Tag Distribution */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm mb-8">
          <h3 className="font-display font-bold text-lg mb-4" style={{ color: "#101010" }}>Distribuzione per Tag</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { key: "investor-ready", value: stats?.investor_ready || 0 },
              { key: "bank-ready", value: stats?.bank_ready || 0 },
              { key: "in-preparazione", value: stats?.in_preparation || 0 },
              { key: "early-stage", value: stats?.early_stage || 0 },
            ].map(item => {
              const tc = tagColors[item.key];
              return (
                <div key={item.key} className="rounded-lg p-4 cursor-pointer transition-all hover:scale-105"
                  onClick={() => setFilter(filter === item.key ? "" : item.key)}
                  style={{
                    background: tc.bg,
                    border: filter === item.key ? `2px solid ${tc.color}` : "2px solid transparent"
                  }}>
                  <div className="font-semibold text-sm" style={{ color: tc.color }}>{tc.label}</div>
                  <div className="font-display font-bold text-3xl mt-1" style={{ color: tc.color }}>{item.value}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-display font-bold text-lg" style={{ color: "#101010" }}>
              Lead Raccolti {filter && `(${tagColors[filter]?.label})`}
            </h3>
            {filter && (
              <button onClick={() => setFilter("")}
                className="text-xs px-3 py-1 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200">
                Mostra tutti
              </button>
            )}
          </div>

          {filteredLeads.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              <div className="text-4xl mb-3">📭</div>
              <p>Nessun lead trovato</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Nome</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Startup</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Inv.</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Bank</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tag</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Data</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredLeads.map(lead => {
                    const tc = tagColors[lead.tag];
                    return (
                      <tr key={lead.id} className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setSelectedLead(lead)}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{lead.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{lead.startup}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{lead.email}</td>
                        <td className="px-6 py-4 text-center">
                          <span className="font-semibold" style={{ color: "#F57C00" }}>{lead.inv_score}%</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="font-semibold text-green-500">{lead.bank_score}%</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold"
                            style={{ background: tc?.bg, color: tc?.color }}>
                            {tc?.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(lead.created_at).toLocaleDateString('it-IT')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Lead Detail Modal */}
        {selectedLead && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedLead(null)}>
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6"
              onClick={e => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-display font-bold text-xl" style={{ color: "#101010" }}>{selectedLead.name}</h3>
                  <p className="text-gray-500 text-sm">{selectedLead.startup}</p>
                </div>
                <button onClick={() => setSelectedLead(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
              </div>

              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-1 rounded-xl p-4" style={{ background: "#FFF3E0" }}>
                    <div className="text-xs text-gray-500 mb-1">Investor Score</div>
                    <div className="font-display font-bold text-2xl" style={{ color: "#F57C00" }}>
                      {selectedLead.inv_score}%
                    </div>
                  </div>
                  <div className="flex-1 rounded-xl p-4 bg-green-50">
                    <div className="text-xs text-gray-500 mb-1">Bank Score</div>
                    <div className="font-display font-bold text-2xl text-green-500">
                      {selectedLead.bank_score}%
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">Email</div>
                  <div className="text-sm font-medium">{selectedLead.email}</div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">Tag</div>
                  <span className="inline-block rounded-full px-3 py-1 text-xs font-semibold"
                    style={{
                      background: tagColors[selectedLead.tag]?.bg,
                      color: tagColors[selectedLead.tag]?.color
                    }}>
                    {tagColors[selectedLead.tag]?.label}
                  </span>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">Sequence</div>
                  <div className="text-sm font-medium capitalize">{selectedLead.sequence}</div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">Data Compilazione</div>
                  <div className="text-sm font-medium">
                    {new Date(selectedLead.created_at).toLocaleString('it-IT')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN QUIZ FLOW
// ─────────────────────────────────────────────────────────────────────────────

function QuizFlow() {
  const [phase, setPhase] = useState("intro");
  const [answers, setAnswers] = useState({});
  const [lead, setLead] = useState(null);
  const [scores, setScores] = useState({ invScore: 0, bankScore: 0 });
  const [loading, setLoading] = useState(false);

  function handleStart() { setPhase("quiz"); }
  function handleAnswers(a) { setAnswers(a); }

  function handleQuizComplete(finalAnswers) {
    const s = calcScores(finalAnswers);
    setScores(s);
    setPhase("lead");
  }

  async function handleLead(form) {
    setLead(form);
    setLoading(true);
    try {
      const tag = getTag(scores.invScore, scores.bankScore);
      const sequence = getSequence(scores.invScore, scores.bankScore);

      await createLead({
        name: form.name,
        email: form.email,
        startup: form.startup,
        inv_score: scores.invScore,
        bank_score: scores.bankScore,
        tag,
        sequence,
        answers,
      });
    } catch (err) {
      console.error("Error saving lead:", err);
    } finally {
      setLoading(false);
      setPhase("result");
    }
  }

  function handleRestart() {
    setPhase("intro");
    setAnswers({});
    setLead(null);
    setScores({ invScore: 0, bankScore: 0 });
  }

  return (
    <>
      {phase === "intro" && <PageIntro onStart={handleStart} />}
      {phase === "quiz" && <PageQuiz answers={answers} onAnswer={handleAnswers} onComplete={handleQuizComplete} />}
      {phase === "lead" && <PageLead onSubmit={handleLead} loading={loading} />}
      {phase === "result" && <PageResult lead={lead} {...scores} answers={answers} onRestart={handleRestart} />}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// APP ROOT
// ─────────────────────────────────────────────────────────────────────────────

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<QuizFlow />} />
        <Route path="/dashboard" element={<Dashboard />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
